import { useEffect, useState } from 'react';
import { appReleasesApi, type LatestAppRelease } from '@/api/client';
import { BrandMark, SlashField } from '@/components/BrandMark';
import { formatBytes, formatDate } from '@/lib/format';

/**
 * Public page (no sign-in) where crew download the Android app. Shared from Admin → Crew app
 * as a link or QR code.
 */
export function CrewAppInstall() {
  const [latest, setLatest] = useState<LatestAppRelease | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'none' | 'error'>('loading');

  const load = () => {
    setState('loading');
    appReleasesApi
      .latest()
      .then((r) => {
        setLatest(r);
        setState(r ? 'ready' : 'none');
      })
      .catch(() => setState('error'));
  };

  useEffect(() => {
    document.title = 'Install the DispatchDash crew app';
    load();
  }, []);

  return (
    <div className="install-page">
      <div className="install-hero">
        <BrandMark variant="light" size="md" product="DispatchDash" />
        <h1>Install the DispatchDash crew app</h1>
        <p>For drivers and helpers on Android phones (Android 8 or newer).</p>
      </div>

      <main className="install-card">
        {state === 'loading' && (
          <div className="loading-center"><div className="spinner" /></div>
        )}
        {state === 'error' && (
          <div role="alert">
            <p>We couldn't check for the app right now. Check your internet connection and try again.</p>
            <button type="button" className="install-btn secondary" onClick={load}>Try again</button>
          </div>
        )}
        {state === 'none' && (
          <p>The crew app isn't available to download yet. Please ask the office for help.</p>
        )}
        {state === 'ready' && latest && (
          <>
            {/* Plain link so the browser's own downloader handles the APK. */}
            <a className="install-btn" href="/api/app/android/download" download>
              Download the app
            </a>
            <p className="install-meta">
              Version {latest.versionName} · {formatBytes(latest.sizeBytes)}
              {latest.publishedAt ? ` · updated ${formatDate(latest.publishedAt)}` : ''}
            </p>
            {latest.notes && (
              <div className="install-notes">
                <strong>What's new</strong>
                <p>{latest.notes}</p>
              </div>
            )}
          </>
        )}

        <h2>How to install</h2>
        <ol className="install-steps">
          <li>
            Tap <strong>Download the app</strong>. If Chrome warns that the file might be harmful, tap{' '}
            <strong>Download anyway</strong>.
          </li>
          <li>When the download finishes, tap <strong>Open</strong> (or find it in <strong>Files → Downloads</strong>).</li>
          <li>
            If your phone says installing from this source isn't allowed, tap <strong>Settings</strong>, turn on{' '}
            <strong>Allow from this source</strong> for your browser, then go back.
            <span className="install-hint">
              On some phones this is under Settings → Apps → Special access → Install unknown apps → Chrome.
            </span>
          </li>
          <li>Tap <strong>Install</strong>, then <strong>Open</strong>.</li>
          <li>
            If Google Play Protect asks about the app, tap <strong>More details → Install anyway</strong>.
          </li>
          <li>Allow notifications and location when the app asks — they're needed for trip alerts and the live map.</li>
        </ol>

        <div className="install-signin">
          <strong>Signing in</strong>
          <p>
            Use your <strong>crew email and password</strong> — the same ones you use for DispatchDash. Forgot your
            password? Ask the office to reset it.
          </p>
        </div>

        <p className="install-update">
          When a new version is ready, the app tells you and downloads it from here. You don't need to uninstall first.
        </p>
      </main>

      <div className="install-footer">
        <SlashField />
        <span>11elevenlogistics.ph</span>
      </div>

      <style>{`
        .install-page {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--space-xl) var(--space-md);
          background: var(--color-ink);
        }
        .install-hero, .install-card, .install-footer { width: 100%; max-width: 440px; }
        .install-hero h1 {
          font-family: var(--font-display);
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--color-white);
          letter-spacing: -0.02em;
          margin: var(--space-lg) 0 var(--space-xs);
          line-height: 1.2;
        }
        .install-hero p { color: var(--color-steel); font-size: 0.9375rem; margin: 0 0 var(--space-lg); }
        .install-card {
          background: var(--color-white);
          border-radius: var(--radius-md);
          padding: var(--space-xl) var(--space-lg);
          box-shadow: var(--shadow-md);
          color: var(--color-ink);
        }
        .install-btn {
          display: block;
          width: 100%;
          text-align: center;
          padding: 14px var(--space-md);
          border-radius: var(--radius-sm);
          background: var(--color-signal);
          color: var(--color-ink);
          font-weight: 700;
          font-size: 1.0625rem;
          text-decoration: none;
          border: none;
          cursor: pointer;
        }
        .install-btn.secondary { background: var(--color-sand); font-size: 0.9375rem; margin-top: var(--space-sm); }
        .install-meta { text-align: center; color: var(--color-steel); font-size: 0.875rem; margin: var(--space-sm) 0 0; }
        .install-notes {
          margin-top: var(--space-md);
          padding: var(--space-sm) var(--space-md);
          background: var(--color-sand);
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
        }
        .install-notes p { margin: 4px 0 0; white-space: pre-wrap; }
        .install-card h2 { font-size: 1.125rem; margin: var(--space-xl) 0 var(--space-sm); }
        .install-steps { padding-left: 1.25rem; margin: 0; display: flex; flex-direction: column; gap: var(--space-sm); font-size: 0.9375rem; line-height: 1.45; }
        .install-hint { display: block; color: var(--color-steel); font-size: 0.8125rem; margin-top: 2px; }
        .install-signin {
          margin-top: var(--space-lg);
          padding: var(--space-md);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          font-size: 0.9375rem;
        }
        .install-signin p { margin: 4px 0 0; }
        .install-update { color: var(--color-steel); font-size: 0.8125rem; margin: var(--space-md) 0 0; }
        .install-footer {
          margin-top: var(--space-2xl);
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          color: var(--color-steel);
          font-size: 0.75rem;
          letter-spacing: 0.04em;
        }
      `}</style>
    </div>
  );
}
